import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { ref, computed } from "vue";
import { formatCurrency } from "@/infrastructure/utilities";
import { isChoiceProductRoot } from "@/infrastructure/data-functions";

import ListCategory from "@/components/category/ListCategory";
import RasterCategory from "@/components/category/RasterCategory";
import ListProduct from "@/components/product/ListProduct";
import RasterProduct from "@/components/product/RasterProduct";
import OrderTotaal from "@/components/order/Totaal";
import ProductImageModal from "@/components/product/ImageModal";

import { calcTotal } from "@/infrastructure/order-functions";
import { isHeaderLine } from "../infrastructure/data-functions";

export default {
  components: { ListCategory, RasterCategory, ListProduct, RasterProduct, OrderTotaal, ProductImageModal },
  props: {
    klas: {
      type: Object,
      required: true,
    },
    display: {
      type: String,
      default: "list",
    },
  },
  setup() {
    const submitted = ref(false);
    const store = useStore();
    const router = useRouter();

    const categories = store.state.data.categories;
    const productTree = store.state.data.productTree;
    const categoryProductNodes = computed(() => (categoryId) => productTree.filter((np) => np.value.categoryId === categoryId && np.parent == null));
    const orderlines = computed(() => store.getters["orders/orderlines"]);

    const isRequired = computed(() => store.getters["data/isRequired"]);
    const selectedIds = computed(() => store.getters["orders/selectedIds"]);
    const isOrdered = computed(() => store.getters["orders/isOrdered"]);
    const isValidOrder = computed(() => store.getters["orders/isValidOrder"]);

    // const togglableProducts = computed(() => {
    //   const checkboxProducts = store.getters["data/simpleProductNodes"];
    //   return checkboxProducts.filter((p) => !store.getters["data/isRequired"](p));
    // });
    const togglableProducts = store.getters["data/simpleProductNodes"].filter((p) => !store.getters["data/isRequired"](p));
    const areAllSelected = computed(() => togglableProducts.every((pn) => isHeaderLine(pn) || isOrdered.value(pn)));

    const quantity = computed(() => store.getters["orders/orderedQuantity"]);
    const totalCost = computed(() => (categoryId = null) => calcTotal(orderlines.value, productTree, categoryId));
    const hasDiscount = computed(() => store.getters["orders/hasDiscount"]);
    const showZeroOrderWarning = computed(() => submitted.value && !hasDiscount.value && totalCost.value() <= 0);

    const removeProduct = (productNode) => store.dispatch("orders/removeProduct", productNode);
    const removeProducts = (productNodes) => store.dispatch("orders/removeProducts", productNodes);
    const addProduct = ({ productNode, quantity = productNode.value.requiredQuantity }) => store.dispatch("orders/addProduct", { productNode, quantity });
    const addProducts = (orderlines) => store.dispatch("orders/addProducts", orderlines);

    function toggleChoiceProduct(productId, rootProductNode) {
      const productNodes = productTree.filter((p) => [].concat(productId).includes(p.value.id));
      const orderedQuantity = quantity.value(rootProductNode);
      // remove other siblings first
      const selectedOffspring = rootProductNode.getOffspring().filter((pn) => isOrdered.value(pn, false));
      for (const productNode of selectedOffspring) {
        removeProduct(productNode);
      }
      // add selected product
      if (productNodes.length) {
        for (const productNode of productNodes) {
          addProduct({ productNode, quantity: orderedQuantity || productNode.value.requiredQuantity || 1 });
        }
      }
    }
    function toggleSimpleProduct(productId) {
      const productNode = productTree.find((p) => p.value.id === productId);
      if (isOrdered.value(productNode)) {
        removeProduct(productNode);
      } else {
        addProduct({ productNode, quantity: productNode.value.requiredQuantity || 1 });
      }
    }
    const toggleProduct = (productId, rootProductNode) => {
      // dropdown
      if (isChoiceProductRoot(rootProductNode)) {
        toggleChoiceProduct(productId, rootProductNode);
      } else {
        // checkbox
        toggleSimpleProduct(productId);
      }
    };
    const toggleAllProducts = async () => {
      if (areAllSelected.value) {
        const productNodesToRemove = orderlines.value.map((ol) => ol.productId);
        await removeProducts(productNodesToRemove);
      } else {
        const missingProductNodes = togglableProducts.filter((pn) => !isOrdered.value(pn));
        await addProducts(missingProductNodes.map((productNode) => ({ productNode, quantity: 1 })));
      }
    };

    const goBack = () => router.push({ name: "klas" });
    const onSubmit = () => {
      submitted.value = true;
      if ((hasDiscount.value || totalCost.value() > 0) && isValidOrder.value) {
        router.push({ name: "besteloverzicht" });
      }
    };

    // ImageModal
    const modalProduct = ref(null);

    return {
      categoryComponent: computed(() => (display) => (display === "raster" ? RasterCategory : ListCategory)),
      productComponent: computed(() => (display) => (display === "raster" ? RasterProduct : ListProduct)),

      // state
      submitted,
      areAllSelected,
      categories,
      productTree,
      categoryProductNodes,
      orderlines,

      selectedIds,
      isOrdered,
      isRequired,
      quantity,
      totalCost,
      showZeroOrderWarning,
      quantityEnabled: store.state.webshop.enableClientQuantity,
      isValidOrder,

      // functions
      formatCurrency,
      removeProduct,
      addProduct,
      toggleProduct,

      toggleAllProducts,
      goBack,
      onSubmit,

      // ImageModal
      modalProduct,
    };
  },
};
